import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Search } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal, staggerContainer, staggerItem } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Blogs() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .listBlogs()
      .then(({ posts }) => setPosts(posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(posts.map((p) => p.category)))], [posts]);

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      const matchesCategory = category === "All" || p.category === category;
      const matchesQuery = query.trim() === "" || p.title.toLowerCase().includes(query.trim().toLowerCase());
      return matchesCategory && matchesQuery;
    });
  }, [posts, category, query]);

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="blogs-page">
      <SEO
        title="Knowledge Centre & Business Guides | Biz360 Compliance"
        description="Expert guides on business incorporation, GST compliance, ROC annual filings, and legal regulations for founders and enterprises in India."
        canonical="/blogs"
        keywords="business compliance blog, company registration guides India, GST return advice, ROC filing checklist"
        breadcrumbs={[{ label: "Knowledge Centre", href: "/blogs" }]}
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-16 md:pt-40 md:pb-20">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <Reveal>
                <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                  Knowledge Centre
                </span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-navy-900 md:text-5xl">
                  Guides to registration &amp; compliance in India
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-4 text-base text-slate-600 md:text-lg">
                  In-depth, professionally reviewed guides covering every major business structure — eligibility, documents, timelines and fees, explained simply.
                </p>
              </Reveal>
            </div>

            <Reveal delay={0.15}>
              <div className="mx-auto mt-10 flex max-w-xl items-center gap-2 rounded-full border border-navy-900/10 bg-white px-5 py-3 shadow-[0_8px_24px_rgba(15,30,60,0.06)]">
                <Search className="h-4 w-4 shrink-0 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  type="text"
                  placeholder="Search guides — e.g. LLP, Section 8, GST..."
                  data-testid="blogs-search"
                  className="w-full bg-transparent text-sm text-navy-900 outline-none placeholder:text-slate-400"
                />
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2.5">
                {categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    data-testid={`blogs-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                      category === c ? "bg-navy-900 text-white" : "border border-navy-900/15 bg-white text-navy-800 hover:border-gold-400 hover:text-gold-600"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="relative bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            {loading ? (
              <p className="py-20 text-center text-slate-500">Loading guides...</p>
            ) : filtered.length === 0 ? (
              <p className="py-20 text-center text-slate-500" data-testid="blogs-empty-state">
                No guides match your search. Try a different keyword or category.
              </p>
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: false, margin: "-60px" }}
                className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                {filtered.map((p, i) => (
                  <motion.div key={p.slug} variants={staggerItem}>
                    <Link
                      to={`/blogs/${p.slug}`}
                      data-testid={`blog-card-${i}`}
                      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-[0_20px_44px_rgba(15,30,60,0.12)]"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={p.image}
                          alt={p.title}
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                        <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold backdrop-blur ${i % 2 === 0 ? "bg-navy-900/90 text-gold-400" : "bg-emerald2-600/95 text-white"}`}>
                          {p.category}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <h3 className="text-lg font-semibold leading-snug text-navy-900 transition-colors group-hover:text-gold-600">
                          {p.title}
                        </h3>
                        <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-slate-500">{p.subtitle}</p>
                        <div className="mt-auto flex items-center justify-between pt-5">
                          <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                            <Clock className="h-3.5 w-3.5" /> {p.read_time}
                          </div>
                          <span className="flex items-center gap-1 text-xs font-semibold text-emerald2-600">
                            Read guide <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
